/**
 * The sentence the Convex client rejects an in-flight action with when its WebSocket
 * reconnects (`request_manager.js` `restart()`). Mutations in flight are re-sent and deduped
 * by the server; actions are dropped, because the client cannot know whether they ran. The
 * rejection is a plain `Error` shaped `[CONVEX A(<module>:<fn>)] Connection lost while action
 * was in flight\n  Called by client` — not a `ConvexError`, so it carries no app-error code
 * and this substring is the only signal there is. `convex-action-retry.dependency.test.ts`
 * reads the installed client and fails if it no longer contains this exact sentence.
 */
export const CONVEX_ACTION_CONNECTION_LOST_MESSAGE = "Connection lost while action was in flight";

const DEFAULT_RETRY_DELAYS_MS = [250, 1000, 3000] as const;

export function isConvexActionConnectionLoss(error: unknown): error is Error {
  return error instanceof Error && error.message.includes(CONVEX_ACTION_CONNECTION_LOST_MESSAGE);
}

function abortError() {
  return new DOMException("Upload aborted", "AbortError");
}

/** Resolves after `ms`, or as soon as `signal` aborts — whichever comes first. */
function wait(ms: number, signal: AbortSignal | undefined) {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Re-runs `run` after each connection-loss rejection, once per entry in `delaysMs`, and
 * rethrows the last such rejection when they run out. Any other error is rethrown at once.
 *
 * Only valid for an action whose server side is idempotent on its args: the rejection means
 * the outcome is unknown, so the first attempt may well have completed. There is no wait on
 * the socket itself — a request issued while it is down is queued by the client and sent on
 * reconnect; the delay only keeps a flapping connection from spending every attempt at once.
 *
 * `signal` stops the loop, not the attempt. Once it has aborted, no further attempt starts and
 * a wait between attempts ends early, and at either of those points the call rejects with an
 * `AbortError` `DOMException` — the shape an aborted upload rejects with, so a caller that
 * already ignores its own aborts needs nothing new. An attempt already running when the
 * signal fires is neither recalled nor discarded: if it resolves, the call resolves with its
 * result (the composer relies on that to take back a draft it can no longer show), and if it
 * rejects with a connection loss, the abort is what the loop sees next.
 */
export async function retryActionOnConnectionLoss<T>(
  run: () => Promise<T>,
  options: {
    delaysMs?: readonly number[];
    onRetry?: (attempt: number, error: Error) => void;
    signal?: AbortSignal;
  } = {}
): Promise<T> {
  const { signal } = options;
  const delaysMs = options.delaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  for (let attempt = 0; ; attempt += 1) {
    if (signal?.aborted) throw abortError();
    try {
      return await run();
    } catch (error) {
      const delayMs = delaysMs[attempt];
      if (!isConvexActionConnectionLoss(error) || delayMs === undefined) {
        throw error;
      }
      options.onRetry?.(attempt + 1, error);
      await wait(delayMs, signal);
    }
  }
}
